import type { IceServerConfig } from '../core/model';
import type { EncodingOptions, NetDeps, WhipPublisher, WhipStartOptions, WhipState } from './api';
import { backoffDelay } from './backoff';
import {
  AUTO_CODEC_WAIT_MS,
  BROWSER_DEFAULT,
  browserEncodingInfo,
  browserSendFormats,
  decideAutoCodec,
  encodeTargetOf,
  settledWithin,
} from './codec-choice';
import {
  addProgramTransceivers,
  applyCodecPreference,
  applyEncoding,
  closePeerConnection,
  mergeIceServers,
  replaceProgramTracks,
  type ProgramSenders,
} from './media';
import { createStatsWindow, readPeerStats, type StatsWindow } from './stats';
import { createStore, errorMessage } from './store';
import {
  WHIP_NETWORK_FAILURE,
  buildTrickleFragment,
  describeWhipFailure,
  extractIceInfo,
  isTokenSafeEndpoint,
  isWhipEndpoint,
  parseLinkIceServers,
  resolveResourceUrl,
  tokenForResource,
  whipHeaders,
  type SdpIceInfo,
} from './whip-http';

const SCOPE = 'net.whip';

/**
 * Many WHIP servers do not implement trickle ICE, so the offer is sent with its
 * candidates already in it. Gathering is bounded because an unreachable TURN
 * server would otherwise hold the POST back for tens of seconds.
 */
const ICE_GATHER_TIMEOUT_MS = 3000;
const DISCONNECT_GRACE_MS = 5000;
const CONNECT_TIMEOUT_MS = 20_000;
const DELETE_TIMEOUT_MS = 5000;
const TRICKLE_BATCH_MS = 100;
/** How long stop() lets an in-flight POST finish so that the session it creates can be deleted. */
const STOP_POST_GRACE_MS = 8000;

class WhipError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
  }
}

/** Thrown inside an attempt that was overtaken by stop(); never surfaced. */
class Superseded extends Error {}

interface Link {
  pc: RTCPeerConnection;
  senders: ProgramSenders;
  stats: StatsWindow;
  resourceUrl: string | null;
  etag: string | null;
  posted: boolean;
  ice: SdpIceInfo | null;
  trickle: Array<{ candidate: string; sdpMid: string | null }>;
  trickleEnded: boolean;
  trickleEndSent: boolean;
  trickleDisabled: boolean;
  trickleTimer: ReturnType<typeof setTimeout> | null;
  disconnectTimer: ReturnType<typeof setTimeout> | null;
  watchdog: ReturnType<typeof setTimeout> | null;
}

interface Run {
  options: WhipStartOptions;
  encoding: EncodingOptions;
  stream: MediaStream;
  /** ICE servers learned from the server's Link headers; used from the next attempt on. */
  linkIce: IceServerConfig[];
  link: Link | null;
  attempts: number;
  establishing: boolean;
  /** The exchange in flight, if any. Never rejects. */
  inFlight: Promise<void> | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  abort: AbortController;
}

const IDLE: WhipState = { status: 'idle', resourceUrl: null, error: null, reconnectAttempts: 0 };

function waitForIceGathering(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', onChange);
      resolve();
    };
    const onChange = (): void => {
      if (pc.iceGatheringState === 'complete') done();
    };
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}

export function createWhipPublisher(deps: NetDeps): WhipPublisher {
  const { logger } = deps;
  const store = createStore<WhipState>(IDLE);
  let run: Run | null = null;

  function clearLinkTimers(link: Link): void {
    if (link.trickleTimer) clearTimeout(link.trickleTimer);
    if (link.disconnectTimer) clearTimeout(link.disconnectTimer);
    if (link.watchdog) clearTimeout(link.watchdog);
    link.trickleTimer = link.disconnectTimer = link.watchdog = null;
  }

  /** Ends the session on the server (best effort) and frees the connection. */
  async function discard(r: Run, link: Link): Promise<void> {
    clearLinkTimers(link);
    closePeerConnection(link.pc);
    if (link.resourceUrl === null) return;
    try {
      const res = await fetch(link.resourceUrl, {
        method: 'DELETE',
        headers: whipHeaders(null, tokenForResource(link.resourceUrl, r.options.endpoint, r.options.bearerToken)),
        credentials: 'omit',
        // Lets the request outlive the page when stop() runs during unload.
        keepalive: true,
        signal: AbortSignal.timeout(DELETE_TIMEOUT_MS),
      });
      if (!res.ok) logger.log('warn', SCOPE, `DELETE of the WHIP session answered ${res.status}`);
    } catch (err) {
      logger.log('warn', SCOPE, `could not DELETE the WHIP session: ${errorMessage(err)}`);
    }
  }

  function flushTrickle(r: Run, link: Link): void {
    link.trickleTimer = null;
    // An entity tag on the session is how a server advertises PATCH support (RFC 9725 §4.3.1).
    if (run !== r || link.etag === null || link.resourceUrl === null || link.ice === null || link.trickleDisabled) {
      return;
    }
    const sendEnd = link.trickleEnded && !link.trickleEndSent;
    const body = buildTrickleFragment(link.ice, link.trickle.splice(0), sendEnd);
    if (body === null) return;
    if (sendEnd) link.trickleEndSent = true;
    fetch(link.resourceUrl, {
      method: 'PATCH',
      headers: {
        ...whipHeaders(
          'application/trickle-ice-sdpfrag',
          tokenForResource(link.resourceUrl, r.options.endpoint, r.options.bearerToken),
        ),
        'If-Match': link.etag,
      },
      body,
      credentials: 'omit',
      signal: r.abort.signal,
    })
      .then((res) => {
        if (res.status === 405 || res.status === 501 || res.status === 404) link.trickleDisabled = true;
        if (!res.ok) logger.log('warn', SCOPE, `trickle ICE PATCH answered ${res.status}`);
      })
      .catch((err: unknown) => {
        if (run === r) logger.log('warn', SCOPE, `trickle ICE PATCH failed: ${errorMessage(err)}`);
      });
  }

  function onConnectionState(r: Run, link: Link): void {
    if (run !== r || r.link !== link) return;
    const state = link.pc.connectionState;
    if (state === 'connected') {
      if (link.disconnectTimer) clearTimeout(link.disconnectTimer);
      if (link.watchdog) clearTimeout(link.watchdog);
      link.disconnectTimer = link.watchdog = null;
      r.attempts = 0;
      store.set({ status: 'live', reconnectAttempts: 0, error: null });
      void applyEncoding(link.senders, r.encoding, logger);
    } else if (state === 'failed') {
      reconnect(r, 'connection failed');
    } else if (state === 'disconnected') {
      link.disconnectTimer ??= setTimeout(() => {
        link.disconnectTimer = null;
        if (run === r && r.link === link && link.pc.connectionState !== 'connected') {
          reconnect(r, 'connection lost');
        }
      }, DISCONNECT_GRACE_MS);
    }
  }

  /** One complete WHIP exchange: offer, POST, answer. Throws WhipError or Superseded. */
  async function establish(r: Run): Promise<Link> {
    let codec = r.encoding.codec;
    let efficientFormats: ReadonlySet<string> | undefined;
    if (codec === 'auto') {
      // A WHIP session cannot change codec later, so it is worth waiting a little longer than an
      // offer to a viewer would.
      const decision = await settledWithin(
        decideAutoCodec(encodeTargetOf(r.stream, r.encoding.videoKbps), browserEncodingInfo(), browserSendFormats()),
        AUTO_CODEC_WAIT_MS * 2,
        { ...BROWSER_DEFAULT, reason: 'capability query did not answer in time; browser default order kept' },
      );
      if (run !== r) throw new Superseded();
      logger.log('info', SCOPE, `auto codec: ${decision.reason}`);
      codec = decision.codec;
      efficientFormats = decision.efficientFormats;
    }
    const pc = new RTCPeerConnection({
      iceServers: mergeIceServers(r.options.iceServers, r.linkIce),
      bundlePolicy: 'max-bundle',
    });
    const link: Link = {
      pc,
      senders: addProgramTransceivers(pc, r.stream, 'sendonly', { ...r.encoding, codec }, logger),
      stats: createStatsWindow(),
      resourceUrl: null,
      etag: null,
      posted: false,
      ice: null,
      trickle: [],
      trickleEnded: false,
      trickleEndSent: false,
      trickleDisabled: false,
      trickleTimer: null,
      disconnectTimer: null,
      watchdog: null,
    };
    const alive = (): void => {
      if (run !== r) throw new Superseded();
    };
    // Within the chosen codec, the hardware-encoded formats go first.
    if (efficientFormats && efficientFormats.size > 0) applyCodecPreference(link.senders.video, codec, logger, efficientFormats);

    try {
      pc.onicecandidate = (event) => {
        // Candidates found before the POST travel inside the offer itself.
        if (!link.posted) return;
        if (event.candidate) link.trickle.push({ candidate: event.candidate.candidate, sdpMid: event.candidate.sdpMid });
        else link.trickleEnded = true;
        link.trickleTimer ??= setTimeout(() => flushTrickle(r, link), TRICKLE_BATCH_MS);
      };
      pc.onconnectionstatechange = () => onConnectionState(r, link);

      await pc.setLocalDescription(await pc.createOffer());
      await waitForIceGathering(pc, ICE_GATHER_TIMEOUT_MS);
      alive();
      const offer = pc.localDescription?.sdp ?? '';
      link.ice = extractIceInfo(offer);
      link.posted = true;

      let res: Response;
      try {
        res = await fetch(r.options.endpoint, {
          method: 'POST',
          headers: whipHeaders('application/sdp', r.options.bearerToken),
          body: offer,
          // 307/308 keep method and body, which is what RFC 9725 asks clients to support.
          redirect: 'follow',
          credentials: 'omit',
          cache: 'no-store',
          signal: r.abort.signal,
        });
      } catch {
        alive();
        throw new WhipError(WHIP_NETWORK_FAILURE.message, WHIP_NETWORK_FAILURE.retryable);
      }
      const respondingUrl = res.url !== '' ? res.url : r.options.endpoint;
      // Recorded first so that every later failure path still DELETEs the session it created.
      if (res.ok) link.resourceUrl = resolveResourceUrl(res.headers.get('Location'), respondingUrl);
      alive();
      if (!res.ok) {
        const failure = describeWhipFailure({
          status: res.status,
          redirected: res.redirected,
          url: respondingUrl,
          hadToken: Boolean(r.options.bearerToken?.trim()),
          opaqueRedirect: res.type === 'opaqueredirect',
        });
        throw new WhipError(failure.message, failure.retryable);
      }
      if (res.status !== 201) logger.log('warn', SCOPE, `WHIP server answered ${res.status} instead of 201`);
      if (link.resourceUrl !== null && r.options.bearerToken?.trim() &&
        tokenForResource(link.resourceUrl, r.options.endpoint, r.options.bearerToken) === undefined) {
        logger.log(
          'warn',
          SCOPE,
          'the WHIP session lives on a different origin than the endpoint; the bearer token will not be sent to it',
        );
      }
      if (link.resourceUrl === null) {
        logger.log(
          'warn',
          SCOPE,
          'no usable Location header (missing, or not exposed via Access-Control-Expose-Headers); the session cannot be deleted on stop',
        );
      }
      link.etag = res.headers.get('ETag');
      const advertised = parseLinkIceServers(res.headers.get('Link'));

      const answer = await res.text();
      alive();
      if (!answer.trimStart().startsWith('v=')) {
        throw new WhipError('The WHIP server did not return an SDP answer.', false);
      }
      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      } catch (err) {
        throw new WhipError(`The WHIP server's SDP answer was not accepted: ${errorMessage(err)}`, false);
      }
      alive();

      if (advertised.length > 0) {
        r.linkIce = advertised;
        try {
          // Takes effect for any further gathering on this connection and for later attempts.
          pc.setConfiguration({ ...pc.getConfiguration(), iceServers: mergeIceServers(r.options.iceServers, advertised) });
        } catch (err) {
          logger.log('debug', SCOPE, `setConfiguration with advertised ICE servers failed: ${errorMessage(err)}`);
        }
      }
      await applyEncoding(link.senders, r.encoding, logger);
      alive();
      if (link.trickle.length > 0 || link.trickleEnded) flushTrickle(r, link);
      return link;
    } catch (err) {
      await discard(r, link);
      throw err;
    }
  }

  function adopt(r: Run, link: Link): void {
    r.link = link;
    store.set({ resourceUrl: link.resourceUrl });
    link.watchdog = setTimeout(() => {
      link.watchdog = null;
      if (run === r && r.link === link && link.pc.connectionState !== 'connected') reconnect(r, 'connect timeout');
    }, CONNECT_TIMEOUT_MS);
    // The connection may already have changed state while the exchange was finishing.
    onConnectionState(r, link);
  }

  function fail(r: Run, message: string): void {
    if (run !== r) return;
    run = null;
    if (r.retryTimer) clearTimeout(r.retryTimer);
    r.abort.abort();
    logger.log('error', SCOPE, message);
    store.set({ status: 'error', resourceUrl: null, error: message, reconnectAttempts: r.attempts });
  }

  function reconnect(r: Run, reason: string): void {
    if (run !== r || r.retryTimer || r.establishing) return;
    const old = r.link;
    r.link = null;
    if (old) void discard(r, old);
    const delay = backoffDelay(r.attempts);
    r.attempts += 1;
    logger.log('info', SCOPE, `${reason}; new WHIP session in ${delay} ms (attempt ${r.attempts})`);
    store.set({ status: 'reconnecting', resourceUrl: null, reconnectAttempts: r.attempts });
    r.retryTimer = setTimeout(() => {
      r.retryTimer = null;
      r.establishing = true;
      r.inFlight = establish(r).then(
        (link) => {
          r.establishing = false;
          adopt(r, link);
        },
        (err: unknown) => {
          r.establishing = false;
          if (run !== r || err instanceof Superseded) return;
          if (err instanceof WhipError && !err.retryable) fail(r, err.message);
          else reconnect(r, errorMessage(err));
        },
      );
    }, delay);
  }

  return {
    getState: store.get,
    subscribe: store.subscribe,

    async start(options) {
      if (run) throw new Error('WHIP publishing is already running; stop it first.');
      if (!isWhipEndpoint(options.endpoint)) {
        const message = `"${options.endpoint}" is not an http(s) WHIP endpoint URL.`;
        store.set({ ...IDLE, status: 'error', error: message });
        throw new Error(message);
      }
      if (options.bearerToken?.trim() && !isTokenSafeEndpoint(options.endpoint)) {
        const message =
          'Refusing to send the bearer token over unencrypted http://. Use an https:// WHIP endpoint ' +
          '(plain http is only accepted for localhost).';
        store.set({ ...IDLE, status: 'error', error: message });
        throw new Error(message);
      }
      const r: Run = {
        options,
        encoding: {
          videoKbps: options.videoKbps,
          audioKbps: options.audioKbps,
          codec: options.codec,
          degradation: options.degradation,
        },
        stream: options.stream,
        linkIce: [],
        link: null,
        attempts: 0,
        establishing: true,
        inFlight: null,
        retryTimer: null,
        abort: new AbortController(),
      };
      run = r;
      store.set({ ...IDLE, status: 'connecting' });
      const attempt = establish(r);
      r.inFlight = attempt.then(
        () => {},
        () => {},
      );
      try {
        const link = await attempt;
        r.establishing = false;
        adopt(r, link);
      } catch (err) {
        r.establishing = false;
        if (err instanceof Superseded || run !== r) throw new Error('WHIP publishing was stopped.');
        // The first attempt is not retried: a wrong URL or token should reach the operator at once.
        const message = errorMessage(err);
        fail(r, message);
        throw new Error(message);
      }
    },

    async stop() {
      const r = run;
      run = null;
      if (!r) {
        store.set(IDLE);
        return;
      }
      if (r.retryTimer) clearTimeout(r.retryTimer);
      r.retryTimer = null;
      const link = r.link;
      r.link = null;
      store.set(IDLE);
      if (link) await discard(r, link);
      if (r.establishing && r.inFlight) {
        // Aborting a POST the server has already acted on would leave a session nobody can
        // delete. The attempt notices the stop as soon as the response arrives and deletes
        // what it created; only a server that never answers is cut off.
        let timer: ReturnType<typeof setTimeout> | undefined;
        await Promise.race([
          r.inFlight,
          new Promise<void>((resolve) => {
            timer = setTimeout(resolve, STOP_POST_GRACE_MS);
          }),
        ]);
        clearTimeout(timer);
      }
      // Last, so that only requests which are still pending by now are cancelled.
      r.abort.abort();
    },

    async getStats() {
      const link = run?.link;
      if (!link) return null;
      try {
        return await readPeerStats(link.pc, link.stats);
      } catch (err) {
        logger.log('debug', SCOPE, `getStats failed: ${errorMessage(err)}`);
        return null;
      }
    },

    async replaceStream(stream) {
      const r = run;
      if (!r) return;
      r.stream = stream;
      if (r.link) await replaceProgramTracks(r.link.senders, stream, r.encoding);
    },

    async updateEncoding(options) {
      const r = run;
      if (!r) return;
      if (options.codec !== undefined && options.codec !== r.encoding.codec) {
        // WHIP has no renegotiation; the codec order is fixed by the offer of each session.
        logger.log('info', SCOPE, 'codec preference applies from the next WHIP session');
      }
      r.encoding = { ...r.encoding, ...options };
      if (r.link) await applyEncoding(r.link.senders, r.encoding, logger);
    },
  };
}
